import type {
  ProductsAndCustomerContext,
  WovarCustomer,
  WovarProduct,
} from "@commercekit/api/lib.server";
import { getProducts } from "@commercekit/api/lib.server";
import { getSeoFromDocument, SliceZoneC } from "@commercekit/prismic";
import { getCustomerLoader } from "@commercekit/remix/lib.server";
import { asText } from "@prismicio/helpers";
import type { SliceComponentType } from "@prismicio/react";
import type { LoaderFunction, LoaderFunctionArgs, SerializeFrom } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { initAppLoader } from "lib/initLoader.server";
import { notFound } from "lib/notFound.server";
import { useEffect, useRef } from "react";
import { useWvViewItemList } from "~/hooks/useDataLayer";
import type { PopularProductsSlice } from "prismicio-types";
import { components } from "~/lib/prismic";
import { useAlternateLanguageLinks } from "~/hooks/useAltHref";
import { buildWebSiteSchema, buildOrganizationSchemaFromParams, getBaseUrl } from "~/lib/schema";
import { metaHelper } from "~/lib/meta";
import { getETrustedDataForHelper } from "~/routes-server/load-reviews.server";

export type { headers } from "@commercekit/remix/lib.server";
export { default as ErrorBoundary } from "~/components/atoms/GeneralErrorBoundary";

export const meta = ({ data }: { data?: SerializeFrom<typeof loader> }) => {
  const base = metaHelper({ data }) ?? [];
  const schemas = [];
  if (data?.websiteSchema != null) schemas.push({ "script:ld+json": data.websiteSchema } as const);
  if (data?.organizationSchema != null) schemas.push({ "script:ld+json": data.organizationSchema } as const);
  return [...base, ...schemas];
};

export const loader: LoaderFunction = async (args: LoaderFunctionArgs) => {
  const helper = await initAppLoader(args);
  const { request, session } = helper;
  const pathnameArr = new URL(request.url).pathname.slice(1).split("/");
  const requestUrl = request.url.split("?");
  const canonicalsUrl = requestUrl.length > 1 ? requestUrl[0] : request.url;
  const token = helper.session.getToken();

  let pathname = "";
  if (pathnameArr.length === 1 && pathnameArr[0] !== "") {
    if (!helper.langsOptions.includes(pathnameArr[0])) {
      notFound();
    }
  } else {
    pathname = pathnameArr.slice(1).join("/");
  }

  const [page, layout, etrustedData] = await Promise.all([
    helper.cms.getHomePage(),
    helper.cms.getLayout(),
    getETrustedDataForHelper(helper),
  ]);

  if (page) {
    const seo = getSeoFromDocument(page);

    const homePopularProductsSlice = page?.data?.body?.filter((slice) => {
      return (
        slice.slice_type === "popular_products" &&
        slice.variation === "homePage"
      );
    }) as PopularProductsSlice[];

    const popularProductSlices = [...homePopularProductsSlice]?.flatMap(
      (product) => product.items?.map((item) => item)
    );

    const popularProductsSKUs = popularProductSlices?.map((item) =>
      asText(item?.product_sku)
    );

    // Get customer when token exist
    const [popularProducts, customer] = await Promise.all([
      getProducts(
        helper.request,
        {
          skus: popularProductsSKUs,
        },
        token
      )
        .then(([, res]) => res?.products?.products || [])
        .catch(() => []),
      token ? getCustomerLoader(helper) : null,
    ]);

    const alternateLanguageLinks = useAlternateLanguageLinks(helper);

    const websiteSchema = buildWebSiteSchema({
      name: "Wovar",
      url: getBaseUrl(canonicalsUrl),
    });

    const seoData = seo as { description?: string } | undefined;
    const organizationSchema = buildOrganizationSchemaFromParams({
      canonicalsUrl,
      description: seoData?.description ?? "",
      logoUrl: (layout.data.logo as { url?: string })?.url ?? "",
      socialMedias: (layout.data.socialMedias as Array<{ link: any }>) ?? [],
      languages: helper.languages,
      locale: helper.locale,
      contactText: asText(layout.data.contact as any) ?? "",
      headerRating: etrustedData?.headerAllTimeRating ?? null,
    });

    return json(
      {
        page,
        seo,
        popularProducts: popularProducts as WovarProduct[],
        canonicalsUrl,
        customer,
        currentLocale: helper.locale,
        alternateLanguageLinks,
        websiteSchema,
        organizationSchema,
      },
      { headers: await session.createHeaders() }
    );
  }

  notFound();
};

export const Page = () => {
  const { page, popularProducts, customer, canonicalsUrl, seo } =
    useLoaderData<typeof loader>();
  const gtmViewItemList = useWvViewItemList();
  const hasFiredViewItemList = useRef(false);

  useEffect(() => {
    if (!popularProducts?.length || hasFiredViewItemList.current) return;

    const listenerOpts = { passive: true, capture: true } as const;

    const fireOnce = () => {
      if (hasFiredViewItemList.current) return;
      hasFiredViewItemList.current = true;
      gtmViewItemList(popularProducts);
      window.removeEventListener("scroll", fireOnce, listenerOpts);
      window.removeEventListener("click", fireOnce, listenerOpts);
      window.removeEventListener("keydown", fireOnce, listenerOpts);
      window.removeEventListener("touchstart", fireOnce, listenerOpts);
    };

    window.addEventListener("scroll", fireOnce, listenerOpts);
    window.addEventListener("click", fireOnce, listenerOpts);
    window.addEventListener("keydown", fireOnce, listenerOpts);
    window.addEventListener("touchstart", fireOnce, listenerOpts);

    return () => {
      window.removeEventListener("scroll", fireOnce, listenerOpts);
      window.removeEventListener("click", fireOnce, listenerOpts);
      window.removeEventListener("keydown", fireOnce, listenerOpts);
      window.removeEventListener("touchstart", fireOnce, listenerOpts);
    };
  }, [popularProducts, gtmViewItemList]);

  useEffect(() => {
    if (window.yotpoWidgetsContainer?.initWidgets) {
      setTimeout(() => {
        window.yotpoWidgetsContainer.initWidgets?.();
      }, 15);
    }
  }, [])

  const productsAndCustomerContext: ProductsAndCustomerContext = {
    products: popularProducts,
    customer: customer as WovarCustomer,
  };

  return (
    <SliceZoneC
      slices={page.data.body}
      components={components as Record<string, SliceComponentType>}
      defaultComponent={({ slice }) => (
        <div className="hidden">{slice.slice_type}</div>
      )}
      context={productsAndCustomerContext}
    />
  );
};

export default Page;
